import { useState, useEffect, useMemo, useRef } from 'react';
import { formatScore } from '@/lib/nflScoreFormat';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  nflMatchupsQueryKey, fetchNflMatchups,
  nflSabotageEventsQueryKey, fetchNflSabotageEvents,
  nflSabotageCardsQueryKey, fetchNflSabotageCards,
  hasLiveMatchup, type NFLMatchupRow,
} from '@/lib/nflSharedQueries';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useNFLDemoOptional } from '@/contexts/NFLDemoContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Swords, Trophy, ChevronDown, ChevronUp, Bomb, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import TeamLogo from './TeamLogo';
import NFLMatchupDetail from './NFLMatchupDetail';
import NFLDemoSabotageModal from './NFLDemoSabotageModal';

interface MatchupRow {
  id: string;
  week: number;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  status: string;
  winner_team_id: string | null;
}

interface TeamInfo {
  id: string;
  name: string;
  wins: number;
  losses: number;
  ties: number;
  logo_url: string | null;
}

interface SabotageEventInfo {
  id: string;
  matchup_id: string;
  card_name: string;
  card_rarity: string;
  attacker_team_id: string;
  target_team_id: string;
}

interface SabotageCard {
  id: string;
  name: string;
  description: string | null;
  rarity: string;
  category: string;
}

const RARITY_PILL: Record<string, { bg: string; text: string; border: string }> = {
  common: { bg: 'bg-muted/20', text: 'text-muted-foreground', border: 'border-border/40' },
  rare: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
  legendary: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
};

const CARD_EMOJIS: Record<string, string> = {
  'False Start': '🏴',
  'Pass Interference': '🚩',
  'Roughing the Passer': '💢',
  'Delay of Game': '⏱️',
  'Unnecessary Roughness': '😤',
  'Targeting': '🎯',
  'Intentional Grounding': '⚡',
  'Fumblerooski': '🤹',
};

interface NFLMatchupsPageProps {
  onNavigate?: (tab: string) => void;
  /**
   * SAB-1062: opens the propose-trade flow pre-targeted at a team, optionally with a player
   * pre-added to their side. Threaded from NFLStaging so every surface that can open the
   * player-detail modal can also act on its Trade button -- a surface that cannot supply
   * this simply does not render the button, rather than rendering a dead one.
   */
  onTradeWith?: (teamId: string, playerId?: string) => void;
}

export default function NFLMatchupsPage({ onNavigate, onTradeWith }: NFLMatchupsPageProps = {}) {
  const { currentLeague, currentTeam, currentWeek, season, teams, isLoading: contextLoading } = useNFLLeague();
  const demo = useNFLDemoOptional();
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  // SAB-501 residual: currentWeek resolves async (starts at 1, then the real week
  // once the league + lastCompletedWeek load). The useState seed above captures
  // the early value and never re-syncs, so the tab could stick on a stale week.
  // Re-sync selectedWeek to currentWeek until the user manually picks a week
  // (then respect their choice), matching how the hero/matchup-card read currentWeek.
  const userPickedWeekRef = useRef(false);
  useEffect(() => {
    if (!userPickedWeekRef.current) setSelectedWeek(currentWeek);
  }, [currentWeek]);
  const handleWeekChange = (week: number) => {
    userPickedWeekRef.current = true;
    setSelectedWeek(week);
  };
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Sabotage data (demo catalog only; live events come from the shared query below)
  const [sabotageCards, setSabotageCards] = useState<SabotageCard[]>([]);
  const [showWheelForMatchup, setShowWheelForMatchup] = useState<string | null>(null);

  // Matchups for the selected week — the shared, week-scoped cache source. staleTime 0 so a tab
  // return refetches in the background (cached rows stay on screen, no blank); a live game keeps
  // rows non-final, which gates a 45s poll so scores stay fresh while watched and the poll goes
  // silent once the week is final. Reused by Sabotages under the same key.
  const { data: matchups = [], isPending } = useQuery({
    queryKey: nflMatchupsQueryKey(currentLeague?.id, selectedWeek, season ?? undefined),
    enabled: !!currentLeague,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (hasLiveMatchup(query.state.data as NFLMatchupRow[] | undefined) ? 45_000 : false),
    queryFn: () => fetchNflMatchups(currentLeague!.id, selectedWeek, season!),
  });

  // Active sabotage events for the week (shared raw rows) + the card catalog; names are enriched
  // client-side so the events key stays canonical and shareable.
  const { data: rawEvents = [] } = useQuery({
    queryKey: nflSabotageEventsQueryKey(currentLeague?.id, selectedWeek),
    enabled: !!currentLeague,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: () => fetchNflSabotageEvents(currentLeague!.id, selectedWeek),
  });
  const { data: cardCatalog = [] } = useQuery({
    queryKey: nflSabotageCardsQueryKey,
    staleTime: 5 * 60_000,
    queryFn: fetchNflSabotageCards,
  });
  const sabotageEvents = useMemo<SabotageEventInfo[]>(() => {
    if (rawEvents.length === 0) return [];
    const cardLookup: Record<string, any> = {};
    for (const c of cardCatalog) cardLookup[c.id] = c;
    return rawEvents.map((e) => ({
      id: e.id,
      matchup_id: e.matchup_id ?? '',
      card_name: cardLookup[e.card_id]?.name || 'Unknown',
      card_rarity: cardLookup[e.card_id]?.rarity || 'common',
      attacker_team_id: e.attacker_team_id,
      target_team_id: e.target_team_id,
    }));
  }, [rawEvents, cardCatalog]);

  // Build team lookup
  const teamMap = useMemo(() => {
    const map: Record<string, TeamInfo> = {};
    for (const t of teams) {
      map[t.id] = {
        id: t.id,
        name: t.name,
        wins: t.wins,
        losses: t.losses,
        ties: t.ties,
        logo_url: t.logo_url,
      };
    }
    return map;
  }, [teams]);

  // Load sabotage card catalog for the demo sabotage preview (only in demo mode)
  useEffect(() => {
    if (!demo?.isDemoMode) return;
    supabase
      .from('nfl_sabotage_cards' as any)
      .select('id, name, description, rarity, category')
      .eq('is_active', true)
      .then(({ data }) => {
        if (data) setSabotageCards(data as SabotageCard[]);
      });
  }, [demo?.isDemoMode]);

  // Group sabotage events by matchup
  const sabotageByMatchup = useMemo(() => {
    const map: Record<string, SabotageEventInfo[]> = {};
    for (const ev of sabotageEvents) {
      if (!map[ev.matchup_id]) map[ev.matchup_id] = [];
      map[ev.matchup_id].push(ev);
    }
    return map;
  }, [sabotageEvents]);

  // Separate my matchup from others
  const myMatchup = useMemo(() => {
    if (!currentTeam) return null;
    return matchups.find(
      m => m.home_team_id === currentTeam.id || m.away_team_id === currentTeam.id
    ) || null;
  }, [matchups, currentTeam]);

  const otherMatchups = useMemo(() => {
    if (!myMatchup) return matchups;
    return matchups.filter(m => m.id !== myMatchup.id);
  }, [matchups, myMatchup]);

  const openWaitlist = () => {
    setShowWheelForMatchup(null);
    document.getElementById('nfl-demo-waitlist-trigger')?.click();
  };

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague || !currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Swords className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to see matchups</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-display font-bold text-foreground">Matchups</h1>
      </div>

      {/* SAB-525: direct week strip (mirrors NFLSchedulePage) so every week's box
          scores are one-click reachable. The old +/- stepper technically reached
          all weeks, but landed on current_week with the back-arrow disabled, so
          completed past/other weeks were non-obvious. 14 regular-season weeks. */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleWeekChange(Math.max(1, selectedWeek - 1))}
          disabled={selectedWeek <= 1}
          className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors shrink-0"
          aria-label="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 overflow-x-auto no-scrollbar">
          <div className="flex gap-1.5">
            {Array.from({ length: 14 }, (_, i) => i + 1).map(w => (
              <button
                key={w}
                onClick={() => handleWeekChange(w)}
                className={cn(
                  'shrink-0 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all',
                  selectedWeek === w
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : w === currentWeek
                      ? 'bg-primary/10 text-primary border border-primary/30'
                      : 'bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                Wk {w}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={() => handleWeekChange(Math.min(14, selectedWeek + 1))}
          disabled={selectedWeek >= 14}
          className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors shrink-0"
          aria-label="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : matchups.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-full bg-muted/30 flex items-center justify-center mb-4">
              <Swords className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">No matchups scheduled</p>
            <p className="text-xs text-muted-foreground mt-1">Week {selectedWeek} matchups will appear once the schedule is set</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* YOUR MATCHUP — featured with orange highlight */}
          {myMatchup && (
            <div>
              <p className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2">YOUR MATCHUP</p>
              <Card className="border-orange-500/40 ring-1 ring-orange-500/20 bg-card overflow-hidden">
                <div className="space-y-2">
                  {/* Sabotage pills for my matchup */}
                  {(sabotageByMatchup[myMatchup.id] || []).length > 0 && (
                    <div className="px-3 pt-3">
                      <SabotagePillRow
                        events={sabotageByMatchup[myMatchup.id] || []}
                        teamMap={teamMap}
                      />
                    </div>
                  )}
                  <NFLMatchupDetail
          onTradeWith={onTradeWith}
                    matchupId={myMatchup.id}
                    homeTeam={teamMap[myMatchup.home_team_id] || makePlaceholder(myMatchup.home_team_id)}
                    awayTeam={teamMap[myMatchup.away_team_id] || makePlaceholder(myMatchup.away_team_id)}
                    homeScore={Number(myMatchup.home_score) || 0}
                    awayScore={Number(myMatchup.away_score) || 0}
                    status={myMatchup.status}
                    week={selectedWeek}
                    myTeamId={currentTeam.id}
                    scoringType={currentLeague.scoring_type ?? undefined}
                    onNavigate={onNavigate}
                  />
                </div>
              </Card>
            </div>
          )}

          {/* Other Matchups — 2-column grid */}
          {otherMatchups.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-2">
                {myMatchup ? 'Other Matchups' : 'All Matchups'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {otherMatchups.map(m => {
                  const isExpanded = expandedId === m.id;
                  const home = teamMap[m.home_team_id] || makePlaceholder(m.home_team_id);
                  const away = teamMap[m.away_team_id] || makePlaceholder(m.away_team_id);
                  const hScore = Number(m.home_score) || 0;
                  const aScore = Number(m.away_score) || 0;
                  const isFinal = m.status === 'final' || m.status === 'completed';
                  const matchupSabotages = sabotageByMatchup[m.id] || [];

                  if (isExpanded) {
                    return (
                      <div key={m.id} className="col-span-1 sm:col-span-2 space-y-2">
                        <button
                          onClick={() => setExpandedId(null)}
                          className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 mb-1 transition-colors"
                        >
                          <ChevronUp className="h-3 w-3" />
                          Collapse
                        </button>
                        <SabotagePillRow events={matchupSabotages} teamMap={teamMap} />
                        <NFLMatchupDetail
                          matchupId={m.id}
                          homeTeam={home}
                          awayTeam={away}
                          homeScore={hScore}
                          awayScore={aScore}
                          status={m.status}
                          week={selectedWeek}
                          myTeamId={currentTeam.id}
                          scoringType={currentLeague.scoring_type ?? undefined}
                          onTrySabotage={demo?.isDemoMode ? () => setShowWheelForMatchup(m.id) : undefined}
                        />
                      </div>
                    );
                  }

                  return (
                    <MatchupSummaryCard
                      key={m.id}
                      away={away}
                      home={home}
                      awayScore={aScore}
                      homeScore={hScore}
                      status={m.status}
                      isFinal={isFinal}
                      sabotages={matchupSabotages}
                      isDemoMode={!!demo?.isDemoMode}
                      onTrySabotage={() => setShowWheelForMatchup(m.id)}
                      onClick={() => setExpandedId(m.id)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Demo Sabotage Preview Modal */}
      {showWheelForMatchup && sabotageCards.length > 0 && (
        <NFLDemoSabotageModal
          cards={sabotageCards}
          onClose={() => setShowWheelForMatchup(null)}
          onSignUp={openWaitlist}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sabotage Pill Row                                                   */
/* ------------------------------------------------------------------ */

function SabotagePillRow({
  events,
  teamMap,
}: {
  events: SabotageEventInfo[];
  teamMap: Record<string, TeamInfo>;
}) {
  if (events.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {events.map(ev => {
        const r = RARITY_PILL[ev.card_rarity] || RARITY_PILL.common;
        const emoji = CARD_EMOJIS[ev.card_name] || '💣';
        return (
          <Badge
            key={ev.id}
            className={cn(
              'text-[10px] px-2 py-0.5 border gap-1 font-medium',
              r.bg, r.text, r.border
            )}
          >
            <span className="text-xs">{emoji}</span>
            {ev.card_name}
          </Badge>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Compact matchup card for "other matchups" grid                      */
/* ------------------------------------------------------------------ */

function MatchupSummaryCard({
  away, home, awayScore, homeScore, status, isFinal, sabotages, isDemoMode, onTrySabotage, onClick,
}: {
  away: TeamInfo;
  home: TeamInfo;
  awayScore: number;
  homeScore: number;
  status: string;
  isFinal: boolean;
  sabotages: SabotageEventInfo[];
  isDemoMode: boolean;
  onTrySabotage: () => void;
  onClick: () => void;
}) {
  const homeWins = homeScore > awayScore;
  const awayWins = awayScore > homeScore;

  return (
    <Card
      className="bg-card border-border cursor-pointer hover:border-primary/20 transition-all"
      onClick={onClick}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge
              className={cn(
                'text-[9px] border px-1.5 py-0',
                status === 'live' || status === 'in_progress'
                  ? 'bg-green-500/10 text-green-400 border-green-500/20 animate-pulse'
                  : isFinal
                  ? 'bg-muted/10 text-muted-foreground border-border/20'
                  : 'bg-primary/10 text-primary border-primary/20'
              )}
            >
              {status === 'live' || status === 'in_progress' ? 'LIVE' : isFinal ? 'FINAL' : 'UPCOMING'}
            </Badge>
            {sabotages.length > 0 && (
              <Badge className="text-[9px] px-1.5 py-0 bg-damage/10 text-damage border-damage/20 border gap-0.5">
                <Bomb className="h-2.5 w-2.5" />
                {sabotages.length}
              </Badge>
            )}
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </div>

        {/* Sabotage pills */}
        {sabotages.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {sabotages.slice(0, 3).map(ev => {
              const r = RARITY_PILL[ev.card_rarity] || RARITY_PILL.common;
              const emoji = CARD_EMOJIS[ev.card_name] || '💣';
              return (
                <span
                  key={ev.id}
                  className={cn(
                    'inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 rounded-full border font-medium',
                    r.bg, r.text, r.border
                  )}
                >
                  <span className="text-[10px]">{emoji}</span>
                  {ev.card_name}
                </span>
              );
            })}
            {sabotages.length > 3 && (
              <span className="text-[9px] text-muted-foreground">+{sabotages.length - 3} more</span>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          {/* Away row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <TeamLogo
                logoUrl={away.logo_url}
                name={away.name}
                className="h-7 w-7"
                textClassName="text-[10px]"
              />
              <span className="text-sm font-medium truncate text-foreground">
                {away.name}
              </span>
              {isFinal && awayWins && <Trophy className="h-3 w-3 text-amber-400 shrink-0" />}
            </div>
            <span className={cn(
              'text-sm font-bold tabular-nums ml-2',
              isFinal && awayWins ? 'text-primary' : isFinal ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {formatScore(awayScore)}
            </span>
          </div>

          {/* Home row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <TeamLogo
                logoUrl={home.logo_url}
                name={home.name}
                className="h-7 w-7"
                textClassName="text-[10px]"
              />
              <span className="text-sm font-medium truncate text-foreground">
                {home.name}
              </span>
              {isFinal && homeWins && <Trophy className="h-3 w-3 text-amber-400 shrink-0" />}
            </div>
            <span className={cn(
              'text-sm font-bold tabular-nums ml-2',
              isFinal && homeWins ? 'text-primary' : isFinal ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {formatScore(homeScore)}
            </span>
          </div>
        </div>

        {/* Prominent sabotage CTA */}
        {isDemoMode && (
          <button
            onClick={(e) => { e.stopPropagation(); onTrySabotage(); }}
            className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white font-bold text-sm animate-sabotage-glow transition-all"
          >
            <Bomb className="h-4 w-4" />
            Try Sabotage. Shop the Store!
          </button>
        )}
      </CardContent>
    </Card>
  );
}

function makePlaceholder(id: string): TeamInfo {
  return { id, name: 'Unknown', wins: 0, losses: 0, ties: 0, logo_url: null };
}
